import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CallbackForm.module.scss';
import { getAppConfig } from '../../config/config';
import { ReactComponent as TelephoneIcon } from '../../svgs/telephone.svg';
import API from '../../api/phx';
import * as utils from '../../utils/OrderUtils';
import useOpenHours from '../../hooks/useOpenHours';
import '../../translations/i18n';

function CallbackForm({ orderId, orderData, isCustomerAccounts }) {
  const config = getAppConfig();
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    firstName: orderData?.billing_address_fname || '',
    lastName: orderData?.billing_address_lname || '',
    phoneNumber: orderData?.billing_address_phone || '',
    email: orderData?.email || '',
    orderId,
  });
  const countryCode = utils.getCountryCode();
  const openHoursText = utils.getOpenHoursText();

  const [formSuccess, setFormSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, loading, checkOpenHours } = useOpenHours(orderId);

  const validatePhoneNumber = (phoneNumber) => {
    const re = /^\+\d{10,15}$/;
    return re.test(String(phoneNumber));
  };

  useEffect(() => {
    let phoneNumber = orderData?.billing_address_phone;
    if (phoneNumber) {
      phoneNumber = utils.formatPhoneNumber(phoneNumber);
      const isValidNumber = validatePhoneNumber(phoneNumber);
      setIsValid(isValidNumber);
      if (!isValidNumber) {
        setErrors((prevErrors) => ({ ...prevErrors, phoneNumber: t('callback.form_errors.invalid_phone_number', { countryCode }) }));
      } else {
        setFormData({ ...formData, phoneNumber });
      }
    }
  }, [orderData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = utils.formatPhoneNumber(value);
    setFormData({ ...formData, [name]: formattedValue });
    const isValidNumber = validatePhoneNumber(formattedValue);
    setIsValid(isValidNumber);
    setErrors({ ...errors, [name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await checkOpenHours(); // Check open hours again before submitting
    const { phoneNumber } = formData;
    const newErrors = {};
    if (!phoneNumber) newErrors.phoneNumber = t('callback.form_errors.phone_number');
    else if (!validatePhoneNumber(phoneNumber)) newErrors.phoneNumber = t('callback.form_errors.invalid_phone_number', { countryCode });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const callMeRequestUrl = utils.appendGeoParam('/five9/callMeRequest');
      const data = {
        order_id: formData.orderId,
        first_name: formData.firstName || '',
        last_name: formData.lastName || '',
        email: formData.email || '',
        phone_number: formData.phoneNumber,
      };
      const response = await API.post(callMeRequestUrl, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.status === 200) {
        setServerError('');
        setErrors({});
        setFormSuccess(true);
      } else {
        setIsSubmitting(false);
        setServerError(t('callback.form_errors.server_error'));
      }
    } catch (error) {
      setIsSubmitting(false);
      setServerError(t('callback.form_errors.server_error'));
      console.error('Error submitting form:', error);
    }
  };

  if (loading && !isSubmitting) {
    return (
      <div className={styles.loading}>Loading...</div>
    );
  }

  if (!isOpen && !isCustomerAccounts) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.modalBody}>
          <p>{ t('callback.closed') }</p>
          <p>
            { t('callback.open') }
            &nbsp;
            { openHoursText }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      {!formSuccess ? (
        <div className={styles.modalBody}>
          {isOpen && isCustomerAccounts ? (
            <p>{t('callback.callus_text')}</p>
          ) : (
            <p>{t('callback.callus_text_out_of_hours')}</p>
          )}
          {serverError && (<span className={styles.error}>{serverError}</span>)}
          {errors.phoneNumber && <span className={styles.error}>{errors.phoneNumber}</span>}
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <div className={styles.inputWrapper}>
                <input
                  type="tel"
                  className={`${styles.formControl} ${errors.phoneNumber ? styles.fieldError : ''}`}
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber || ''}
                  onChange={handleChange}
                  placeholder={t('callback.phone_number_label')}
                  required
                />
                {formData.phoneNumber && (
                  <label htmlFor="phoneNumber" className={styles.inputLabel}>
                    {t('callback.phone_number_label')}
                  </label>
                )}
                <span className={`${styles.validationMark} ${isValid ? styles.valid : styles.invalid}`}>
                  {isValid ? '✓' : '✗'}
                </span>
              </div>
              <input name="firstName" type="hidden" value={formData.firstName || ''} />
              <input name="lastName" type="hidden" value={formData.lastName || ''} />
              <input name="email" type="hidden" value={formData.email || ''} />
              <input name="orderId" type="hidden" value={orderId} />
              <button type="submit" className={styles.btnCustom} disabled={isSubmitting}>
                <span className={styles.icon}><TelephoneIcon /></span>
                {t('callback.call_me')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.modalBody}>
          <p>{t('callback.form_success')}</p>
        </div>
      )}
    </div>
  );
}

export default CallbackForm;
